import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';
import { Card } from 'react-bootstrap';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

// Default chart options
const defaultOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
    },
  },
};

// Bar Chart Component
export const BarChart = ({ data, title, height = 300, options = {} }) => {
  const chartOptions = {
    ...defaultOptions,
    ...options,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins,
      title: {
        display: true,
        text: title,
      },
    },
  };

  return (
    <Card className="h-100 shadow-sm border-0">
      <Card.Body>
        <div style={{ height: height }}>
          <Bar data={data} options={chartOptions} />
        </div>
      </Card.Body>
    </Card>
  );
};

// Pie Chart Component
export const PieChartJS = ({ data, title, height = 300, options = {} }) => {
  const chartOptions = {
    ...defaultOptions,
    ...options,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins,
      title: {
        display: true,
        text: title,
      },
    },
  };

  return (
    <Card className="shadow-sm border-0">
      <Card.Body>
        <div style={{ height: height }}>
          <Pie data={data} options={chartOptions} />
        </div>
      </Card.Body>
    </Card>
  );
};

// Doughnut Chart Component
export const DoughnutChart = ({ data, title, height = 300, options = {} }) => {
  const chartOptions = {
    ...defaultOptions,
    ...options,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins,
      title: {
        display: true,
        text: title,
      },
    },
  };

  return (
    <Card className="h-100 shadow-sm border-0">
      <Card.Body>
        <div style={{ height: height }}>
          <Doughnut data={data} options={chartOptions} />
        </div>
      </Card.Body>
    </Card>
  );
};

// Line Chart Component
export const LineChart = ({ data, title, height = 300, options = {} }) => {
  const chartOptions = {
    ...defaultOptions,
    ...options,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins,
      title: {
        display: true,
        text: title,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <Card className="h-100 shadow-sm border-0">
      <Card.Body>
        <div style={{ height: height }}>
          <Line data={data} options={chartOptions} />
        </div>
      </Card.Body>
    </Card>
  );
};

// Utility function to generate chart colors
export const generateChartColors = (count) => {
  const colors = [
    '#007bff', // Blue
    '#28a745', // Green
    '#ffc107', // Yellow
    '#dc3545', // Red
    '#6c757d', // Gray
    '#17a2b8', // Cyan
    '#fd7e14', // Orange
    '#6f42c1', // Purple
    '#e83e8c', // Pink
    '#20c997', // Teal
  ];
  
  return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
};

// Utility function to prepare chart data
export const prepareChartData = (labels, datasets, colors) => {
  return {
    labels,
    datasets: datasets.map((dataset, index) => ({
      ...dataset,
      backgroundColor: colors || generateChartColors(dataset.data.length),
      borderColor: colors || generateChartColors(dataset.data.length),
      borderWidth: 1,
    })),
  };
};